import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { formatApiError } from '@/lib/formatApiError';
import { fetchStoryContext } from '@/lib/creationPlan';
import { UNIFIED_PIPELINE_STEPS, pipelineStepStatus } from '@/lib/unifiedStoryPipeline';

function DebugRow({ label, value }) {
  const text = value == null || value === '' ? '—' : String(value);
  return (
    <div className="grid grid-cols-[7.5rem_1fr] gap-2 text-[11px]">
      <span className="text-[#6B7280]">{label}</span>
      <span className="text-[#E5E7EB] break-all">{text}</span>
    </div>
  );
}

export function UnifiedStoryPipelineDialog({ open, onClose, plan }) {
  const workflow = plan?.story_workflow || {};
  const [context, setContext] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !plan?.id) return undefined;
    let cancelled = false;
    setError('');
    fetchStoryContext(plan.id)
      .then((data) => {
        if (!cancelled) setContext(data?.context || null);
      })
      .catch((err) => {
        if (!cancelled) setError(formatApiError(err, 'Could not load debug details'));
      });
    return () => {
      cancelled = true;
    };
  }, [open, plan?.id, workflow.draft_version_id, workflow.state]);

  if (!open) return null;

  const graph = context?.graph || {};
  const entities = graph.entities || [];
  const manifest = context?.manifest || workflow.manifest || [];
  const screenplay = workflow.structured_screenplay || {};
  const generation = workflow.generation || {};

  return (
    <div
      className="fixed inset-0 z-[230] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      role="presentation"
      data-testid="unified-story-pipeline-popup"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Story debug details"
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col rounded-2xl border border-white/15 bg-[#0c1018] shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 p-4 border-b border-white/10">
          <div>
            <p className="text-sm font-semibold text-white">Debug details</p>
            <p className="text-[11px] text-[#9CA3AF] mt-1">Read-only. Questions stay in the chat.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full border border-white/10 text-[#9CA3AF] hover:text-white flex items-center justify-center cursor-pointer"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-4 overflow-y-auto flex-1 text-xs">
          {error ? <p className="text-red-300">{error}</p> : null}
          <div className="space-y-1">
            {UNIFIED_PIPELINE_STEPS.map((step, index) => (
              <p key={step.id} className="text-[11px] text-[#9CA3AF]">
                {index + 1}. {step.label} · {pipelineStepStatus(workflow, index)}
              </p>
            ))}
          </div>
          <div className="space-y-1 rounded-xl border border-white/10 p-3">
            <DebugRow label="State" value={workflow.state} />
            <DebugRow label="Step" value={workflow.step} />
            <DebugRow label="Draft version" value={workflow.draft_version_id} />
            <DebugRow label="Locked version" value={workflow.locked_version_id} />
            <DebugRow label="Format id" value={workflow.story_format?.format_id} />
            <DebugRow label="Format status" value={workflow.story_format?.status} />
            <DebugRow label="Screenplay id" value={screenplay.screenplay_id || workflow.screenplay_version_id} />
            <DebugRow label="Screenplay" value={screenplay.status} />
            <DebugRow label="Context version" value={context?.version_id} />
          </div>
          {workflow.reconfirm_notice || workflow.format_stale_notice || workflow.screenplay_stale_notice ? (
            <div className="rounded-xl border border-amber-400/30 p-3 space-y-1 text-amber-100">
              {workflow.reconfirm_notice ? <p>{workflow.reconfirm_notice}</p> : null}
              {workflow.format_stale_notice ? <p>{workflow.format_stale_notice}</p> : null}
              {workflow.screenplay_stale_notice ? <p>{workflow.screenplay_stale_notice}</p> : null}
            </div>
          ) : null}
          <div>
            <p className="text-[10px] uppercase tracking-wide text-[#6B7280] mb-1">Sources</p>
            {(manifest.length ? manifest : [{ filename: 'none' }]).map((item) => (
              <p key={item.source_id || item.filename} className="text-[11px] text-[#E5E7EB]">
                {item.filename || item.source_type || item.source_id} · {item.status || '—'}
              </p>
            ))}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-[#6B7280] mb-1">Entities</p>
            {entities.length ? entities.map((entity) => (
              <p key={entity.entity_id} className="text-[11px] text-[#E5E7EB]">
                {entity.display_name} · {entity.entity_type || 'entity'} · {entity.review_status || '—'} · {entity.provenance || '—'}
              </p>
            )) : <p className="text-[11px] text-[#6B7280]">None</p>}
          </div>
          {Object.keys(generation).length ? (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-[#6B7280] mb-1">Generation</p>
              {Object.entries(generation).map(([beatId, row]) => (
                <p key={beatId} className="text-[11px] text-[#E5E7EB] break-all">
                  {beatId}: {row?.status || 'pending'}{row?.runner ? ` · ${row.runner}` : ''}
                </p>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default UnifiedStoryPipelineDialog;
